import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Alert,
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import api from "app/api";
import { ROLES } from "app/constants";
import useAccess from "features/profile/useAccess";
import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

// Type for the API response - events that are over online limit but not closed
type OverLimitEventItem = {
  id: number;
  start_time: string; // event_group.start_time
  program_name: string; // program.name
};

type OverLimitOptionItem = {
  id: number;
  name: string;
  limit: number;
  number_of_bookings: number;
  closed: boolean;
  online_over_limit: boolean;
};

type OverLimitOptionEventItem = {
  id: number;
  start_time: string;
  program_name: string;
  options: OverLimitOptionItem[];
};

type OverOnlineLimitResponse = {
  events?: OverLimitEventItem[];
  option_events?: OverLimitOptionEventItem[];
  closed_webshop_bookings?: ClosedWebshopBookingItem[];
};

type ClosedWebshopBookingItem = {
  booking_id: number;
  booking_number: string;
  event_id: number;
  start_time?: string;
  boat_name?: string;
  program_name?: string;
  variant_name?: string;
  number_of_guests?: number;
  reasons: string[];
  received_at: string;
};

const GlobalOnlineLimitAlert: React.FC = () => {
  const { hasPermissions, hasRoles } = useAccess();
  const [events, setEvents] = useState<OverLimitEventItem[]>([]);
  const [optionEvents, setOptionEvents] = useState<OverLimitOptionEventItem[]>(
    [],
  );
  const [closedWebshopBookings, setClosedWebshopBookings] = useState<
    ClosedWebshopBookingItem[]
  >([]);
  const [acknowledgingBookingId, setAcknowledgingBookingId] = useState<
    number | null
  >(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef(false);
  const canPoll = hasPermissions("booking.create") && !hasRoles(ROLES.PARTNER);
  const canPollRef = useRef(canPoll);
  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    canPollRef.current = canPoll;
  }, [canPoll]);

  // API call to fetch events over online limit
  const fetchAvailableEvents = useCallback(async () => {
    if (!canPollRef.current || isPollingRef.current || !canPoll) {
      return;
    }

    // Only fetch if tab is active
    if (document.visibilityState !== "visible") {
      // Schedule next check when tab becomes visible or after timeout
      timeoutRef.current = setTimeout(fetchAvailableEvents, 20000);
      return;
    }

    isPollingRef.current = true;

    try {
      const { success, data } = await api.get("/events/over-online-limit");
      if (success && data) {
        if (Array.isArray(data)) {
          setEvents(data);
          setOptionEvents([]);
          setClosedWebshopBookings([]);
        } else {
          const response = data as OverOnlineLimitResponse;
          setEvents(Array.isArray(response.events) ? response.events : []);
          setOptionEvents(
            Array.isArray(response.option_events) ? response.option_events : [],
          );
          setClosedWebshopBookings(
            Array.isArray(response.closed_webshop_bookings)
              ? response.closed_webshop_bookings
              : [],
          );
        }
      } else {
        setEvents([]);
        setOptionEvents([]);
        setClosedWebshopBookings([]);
      }
    } catch (error) {
      console.error("Failed to fetch over-limit events:", error);
      setEvents([]);
      setOptionEvents([]);
      setClosedWebshopBookings([]);
    } finally {
      isPollingRef.current = false;
      // Schedule next poll after current one completes
      if (canPollRef.current) {
        timeoutRef.current = setTimeout(fetchAvailableEvents, 20000);
      }
    }
  }, [canPoll]);

  // Set up polling when component mounts or permissions change
  useEffect(() => {
    if (canPoll) {
      // Start initial fetch
      fetchAvailableEvents();
    }

    // Handle visibility change - resume polling when tab becomes active
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && canPollRef.current) {
        // Clear any existing timeout and start fresh when tab becomes visible
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        fetchAvailableEvents();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup timeout and event listener on unmount or permission change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      isPollingRef.current = false;
    };
  }, [canPoll, fetchAvailableEvents]);

  // Don't render if no permissions or no events
  if (
    !hasPermissions("booking.create") ||
    (events.length === 0 &&
      optionEvents.length === 0 &&
      closedWebshopBookings.length === 0)
  ) {
    return null;
  }

  const acknowledgeClosedWebshopBooking = async (bookingId: number) => {
    setAcknowledgingBookingId(bookingId);
    try {
      const { success, data } = await api.post<{ success: boolean }>(
        `/events/closed-webshop-bookings/${bookingId}/acknowledge`,
      );
      if (success && data?.success) {
        setClosedWebshopBookings(current =>
          current.filter(item => item.booking_id !== bookingId),
        );
      }
    } finally {
      setAcknowledgingBookingId(null);
    }
  };

  // Group events by start time for more compact display
  const groupedEvents = events.reduce(
    (acc, event) => {
      const time = moment(event.start_time).format("L LT");
      if (!acc[time]) {
        acc[time] = [];
      }
      acc[time].push(event.program_name);
      return acc;
    },
    {} as Record<string, string[]>,
  );

  // Create a simple list of all events with times
  const eventsList = Object.entries(groupedEvents)
    .map(([time, programs]) => `${time}: ${programs.join(", ")}`)
    .join("; ");

  const optionEventsList = optionEvents
    .map(optionEvent => {
      const time = moment(optionEvent.start_time).format("L LT");
      const optionNames = optionEvent.options
        .filter(option => option.online_over_limit)
        .map(option => option.name)
        .join(", ");
      return `${time}: ${optionEvent.program_name} (${optionNames})`;
    })
    .join("; ");

  const alertSx = {
    borderRadius: 0,
    mb: 0,
    mx: mdDown ? 0 : -3,
  };

  return (
    <>
      {closedWebshopBookings.length > 0 && (
        <Alert
          severity="error"
          variant="filled"
          icon={<ErrorOutlineIcon />}
          sx={{
            ...alertSx,
            backgroundColor: theme.palette.error.dark,
            color: theme.palette.error.contrastText,
          }}
        >
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Webshop-foglalás érkezett lezárt járatra!</strong>
          </Typography>
          {closedWebshopBookings.map(booking => (
            <Box
              key={booking.booking_id}
              sx={{
                display: "flex",
                alignItems: { xs: "flex-start", sm: "center" },
                flexDirection: { xs: "column", sm: "row" },
                gap: 1,
                py: 0.5,
              }}
            >
              <Typography variant="body2" sx={{ flex: 1 }}>
                <strong>{booking.booking_number}</strong>
                {booking.start_time
                  ? `, ${moment(booking.start_time).format("L LT")}`
                  : ""}
                {booking.boat_name ? `, ${booking.boat_name}` : ""}
                {booking.program_name ? `, ${booking.program_name}` : ""}
                {booking.variant_name ? ` / ${booking.variant_name}` : ""}
                {booking.number_of_guests
                  ? `, ${booking.number_of_guests} fő`
                  : ""}
                {booking.reasons?.length
                  ? ` (${booking.reasons.join(" ")})`
                  : ""}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                <Button
                  component={RouterLink}
                  to={`/bookings/${booking.booking_id}`}
                  color="inherit"
                  size="small"
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                >
                  Foglalás
                </Button>
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  startIcon={<TaskAltIcon />}
                  disabled={acknowledgingBookingId === booking.booking_id}
                  onClick={() =>
                    acknowledgeClosedWebshopBooking(booking.booking_id)
                  }
                >
                  Kezelve
                </Button>
              </Box>
            </Box>
          ))}
        </Alert>
      )}
      {(events.length > 0 || optionEvents.length > 0) && (
        <Alert
          severity="warning"
          variant="filled"
          icon={<WarningIcon />}
          sx={alertSx}
        >
          <Typography variant="body2">
            <strong>Online limit túllépés!</strong>
            {events.length > 0 && (
              <>
                {" "}
                {events.length} esemény zárásra vár: {eventsList}
              </>
            )}
            {events.length > 0 && optionEvents.length > 0 && <>; </>}
            {optionEvents.length > 0 && (
              <>
                {optionEvents.length} opció limitet ér el: {optionEventsList}
              </>
            )}
          </Typography>
        </Alert>
      )}
    </>
  );
};

export default GlobalOnlineLimitAlert;
